<template>
  <div class="page-slot" ref="pageSlot">
    <video ref="video" autoplay muted playsinline></video>
    <canvas ref="overlayCanvas" class="overlay-canvas"></canvas>
    <button class="fullscreen-btn" @click="goFullscreen">⛶</button>
    <!-- Add your own controls here -->
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from "@vue/composition-api";
import axios from "@axios";

const VIDEO_ORIGINAL_WIDTH = 860; // <-- set this to your real video width
const VIDEO_ORIGINAL_HEIGHT = 480; // <-- set this to your real video height

export default {
  props: {
    data: {
      type: Object,
      required: true
    },
  },
  setup(props) {
    const urlStream = ref(null);
    const video = ref(null);
    let mediaSource = null;
    let sourceBuffer = null;
    let ws = null;
    let intervalId = null;
    const overlayCanvas = ref(null);
    const faceBbox = ref(null);
    const pageSlot = ref(null);

    const BUFFER_BACK = 4;
    const MAX_LAG = 6;

    const appendNext = (queue) => {
      if (queue.length && !sourceBuffer.updating) {
        sourceBuffer.appendBuffer(queue.shift());
      }
    };

    const drawFaceRect = () => {
      const canvas = overlayCanvas.value;
      const vid = video.value;
      if (!canvas || !vid) return;

      const elementWidth = vid.clientWidth;
      const elementHeight = vid.clientHeight;

      canvas.width = elementWidth;
      canvas.height = elementHeight;
      canvas.style.width = `${elementWidth}px`;
      canvas.style.height = `${elementHeight}px`;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (faceBbox.value) {
        const [x, y, w, h] = faceBbox.value;

        let scale, offsetX, offsetY;
        if (elementWidth > elementHeight) {
          // Fit to height, center horizontally
          scale = elementHeight / VIDEO_ORIGINAL_HEIGHT;
          const videoDisplayWidth = VIDEO_ORIGINAL_WIDTH * scale;
          offsetX = (elementWidth - videoDisplayWidth) / 2;
          offsetY = 0;
        } else {
          // Fit to width, center vertically
          scale = elementWidth / VIDEO_ORIGINAL_WIDTH;
          const videoDisplayHeight = VIDEO_ORIGINAL_HEIGHT * scale;
          offsetX = 0;
          offsetY = (elementHeight - videoDisplayHeight) / 2;
        }

        ctx.beginPath();
        ctx.strokeStyle = '#FF0000';
        ctx.lineWidth = 5;
        ctx.strokeRect(
          offsetX + x * scale,
          offsetY + y * scale,
          w * scale,
          h * scale
        );
        ctx.stroke();
      }
    };

    const initializePlayer = (streamUrl) => {
      mediaSource = new MediaSource();
      video.value.src = URL.createObjectURL(mediaSource);

      mediaSource.addEventListener('sourceopen', () => {
        sourceBuffer = mediaSource.addSourceBuffer('video/mp4; codecs="avc1.42E01E"');
        ws = new WebSocket(streamUrl);
        ws.binaryType = 'arraybuffer';

        const queue = [];
        ws.onmessage = (event) => {
          if (typeof event.data === 'string') {
            try {
              const msg = JSON.parse(event.data);
              if (msg.data && msg.data.faces && msg.data.faces.length > 0) {
                faceBbox.value = msg.data.faces[0].bbox;
                drawFaceRect();
              } else {
                faceBbox.value = null;
                drawFaceRect();
              }
            } catch (e) {}
            return;
          }
          queue.push(new Uint8Array(event.data));
          appendNext(queue);
        };

        sourceBuffer.addEventListener('updateend', () => {
          appendNext(queue);
          const currentTime = video.value.currentTime;
          try {
            if (video.value.buffered.length && video.value.buffered.start(0) < currentTime - BUFFER_BACK) {
              sourceBuffer.remove(video.value.buffered.start(0), currentTime - BUFFER_BACK);
            }
          } catch (e) {}
        });
      });

      // Stay close to live, but allow up to MAX_LAG seconds of lag
      intervalId = setInterval(() => {
        if (video.value.buffered.length) {
          const bufferedEnd = video.value.buffered.end(video.value.buffered.length - 1);
          const currentTime = video.value.currentTime;
          const lag = bufferedEnd - currentTime;
          if (lag > MAX_LAG) {
            video.value.currentTime = Math.max(bufferedEnd - 0.5, 0);
          }
        }
      }, 500);
    };

    const handleResize = () => drawFaceRect();

    const goFullscreen = () => {
      const el = pageSlot.value;
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) { // Safari
        el.webkitRequestFullscreen();
      } else if (el.mozRequestFullScreen) { // Firefox
        el.mozRequestFullScreen();
      } else if (el.msRequestFullscreen) { // IE/Edge
        el.msRequestFullscreen();
      }
    };

    onMounted(() => {
      axios.get(`/v1/ntechlab_token_stream/${props.data.camera.id_camera_ntechlab}`).then((response) => {
        urlStream.value = response.data.data;
        initializePlayer(urlStream.value);
      });
      window.addEventListener('resize', handleResize);
      nextTick(() => drawFaceRect());
    });

    onUnmounted(() => {
      if (ws) {
        ws.close();
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (mediaSource && mediaSource.readyState === 'open') {
        mediaSource.endOfStream();
      }
      window.removeEventListener('resize', handleResize);
    });

    return {
      video,
      urlStream,
      overlayCanvas,
      goFullscreen,
      pageSlot,
    };
  },
};
</script>

<style lang="scss" scoped>
.page-slot {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: black;
  position: relative;

  video {
    width: 100%;
    height: auto;
    display: block;

    &::-webkit-media-controls-panel {
      display: flex !important;
      opacity: 1 !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    &::-webkit-media-controls-fullscreen-button {
      display: block !important;
    }

    &::-webkit-media-controls-play-button,
    &::-webkit-media-controls-timeline,
    &::-webkit-media-controls-current-time-display,
    &::-webkit-media-controls-time-remaining-display,
    &::-webkit-media-controls-mute-button,
    &::-webkit-media-controls-volume-slider {
      display: none !important;
    }

    &::-webkit-media-controls-enclosure {
      background: transparent !important;
      box-shadow: none !important;
    }

    /* Hide loading spinner */
    &::-webkit-media-controls-loading-panel {
      display: none !important;
    }

    /* Hide loading indicator */
    &::-webkit-media-controls-loading-indicator {
      display: none !important;
    }
  }
}

.overlay-canvas {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  pointer-events: none;
  width: 100%;
  height: auto;
}

.fullscreen-btn {
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 10;
  background: rgba(0,0,0,0.5);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 20px;
}
</style>
